import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import LinearProgress from '@mui/material/LinearProgress';
import { ErrorMessage } from '@hookform/error-message';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Tooltip } from '@mui/material';

import { getSubGroupsByGroupIdThunk } from '../../../store/subGroups/subGroupsSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { postCreateNewSubGroup } from '../../../api/subGroupsApi';
import styles from './CreateSubGroupModal.module.scss';
import SearchInput from '../../modules/SearchInput';
import { NOTIFICATION } from '../../../enums/ENUMS';
import RolePlate from '../../modules/RolePlate';
import { RolesLowerCase } from '../../../enums';
import { USER } from '../../../enums/TYPES';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TCreateSubGroupModal = {
  open: boolean;
  switchModal: () => void;
};

type TFormData = {
  title: string;
  groupId: string;
  analysts: string[];
  viewers: string[];
};

const CreateSubGroupModal: React.FC<TCreateSubGroupModal> = ({ open, switchModal }) => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { currentGroup, subGroups, sort, sortOption } = useAppSelector((state) => state.subGroupsStore);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<TFormData>();
  const [errMsg, setErrMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewers, setViewers] = useState<USER[]>([]);
  const [analysts, setAnalysts] = useState<USER[]>([]);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const toFourthStepButtonRef = useRef<HTMLDivElement>(null);
  const backToFirstStepButtonRef = useRef<HTMLDivElement>(null);
  const backToSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const backToThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setViewers(currentGroup?.viewers ?? []);
    setAnalysts(currentGroup?.analysts ?? []);
  }, [currentGroup]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    defaultValues: {
      title: '',
      analysts: [],
      viewers: [],
      groupId: location.state as string,
    },
  });

  const [selectAllOa, setSelectAllOa] = useState(false);
  const [selectAllAnalyst, setSelectAllAnalyst] = useState(false);
  const [selectAllViewer, setSelectAllViewer] = useState(false);

  const toAnalystStep = () => {
    analysts.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).length ? setStep(3) : toViewersStep();
  };

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>, role: RolesLowerCase) => {
    setErrMsg('');
    console.log(analysts.filter((u) => !u?.role?.title));
    console.log(viewers.filter((u) => !u?.role?.title));
    if (role === RolesLowerCase.ORGANIZATION_ADMIN) {
      const oasIds = analysts.filter((oa) => oa.role.title === RolesLowerCase.ORGANIZATION_ADMIN).map((oa) => oa.id);
      if (form?.analysts) {
        const newOas = [...new Set([...form.analysts, ...oasIds])];
        setForm({
          ...form,
          analysts: !selectAllOa ? newOas : form.analysts.filter((oa) => !oasIds.includes(oa)),
        });
        setSelectAllOa(!selectAllOa);
      }
    } else if (role === RolesLowerCase.ANALYST) {
      const analystIds = analysts.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).map((analyst) => analyst.id);
      if (form?.analysts) {
        const newAnalysts = [...new Set([...form.analysts, ...analystIds])];
        setForm({
          ...form,
          analysts: !selectAllAnalyst ? newAnalysts : form.analysts.filter((analyst) => !analystIds.includes(analyst)),
        });
        setSelectAllAnalyst(!selectAllAnalyst);
      }
    } else if (role === RolesLowerCase.VIEWER) {
      const viewerIds = viewers.filter((viewer) => viewer.role.title === RolesLowerCase.VIEWER).map((viewer) => viewer.id);
      if (form?.viewers) {
        const newViewers = [...new Set([...form.viewers, ...viewerIds])];
        setForm({
          ...form,
          viewers: !selectAllViewer ? newViewers : form.viewers.filter((viewer) => !viewerIds.includes(viewer)),
        });
        setSelectAllViewer(!selectAllViewer);
      }
    }
  };

  const onAnalystAdd = (e: any) => {
    setErrMsg('');
    if (form?.analysts) {
      setForm({
        ...form,
        analysts: form?.analysts.includes(e.target.id)
          ? form.analysts.filter((id) => id !== e.target.id)
          : [...form!.analysts, e.target.id],
      });
    }
  };

  const onViewerAdd = (e: any) => {
    setErrMsg('');
    if (form?.viewers) {
      setForm({
        ...form,
        viewers: form?.viewers.includes(e.target.id) ? form.viewers.filter((id) => id !== e.target.id) : [...form!.viewers, e.target.id],
      });
    }
  };

  const toOrgAdminStep = (data: TFormData) => {
    const isTitleTaken = subGroups?.some((group) => group.title.toLowerCase() === data.title.toLowerCase());
    if (isTitleTaken) {
      notification.showMessage(NOTIFICATION.NAME_ALREADY_EXISTS);
      return;
    }

    setStep(2);
    setForm(data);
  };

  const toViewersStep = () => {
    // @ts-ignore
    viewers.length ? setStep(4) : onSubmit();
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>, type: 'analyst' | 'viewer') => {
    if (!e.target.value) {
      setAnalysts(currentGroup?.analysts ?? []);
      setViewers(currentGroup?.viewers ?? []);
      return;
    }
    if (type === 'analyst') {
      setAnalysts(
        analysts.filter((u) => u.email.toLowerCase().includes(e.target.value) || u.username.toLowerCase().includes(e.target.value)),
      );
    }
    if (type === 'viewer') {
      setViewers(
        viewers.filter((u) => u.email.toLowerCase().includes(e.target.value) || u.username.toLowerCase().includes(e.target.value)),
      );
    }
  };

  const onClose = () => {
    reset();
    switchModal();
    setStep(1);
    setSelectAllOa(false);
    setSelectAllAnalyst(false);
    setSelectAllViewer(false);
    setAllDone(false);
  };

  const [allDone, setAllDone] = useState(false);
  const onSubmit = async () => {
    try {
      setLoading(true);
      await postCreateNewSubGroup(form!);
      setAllDone(true);
      dispatch(getSubGroupsByGroupIdThunk({ groupId: location.state as string, sort, sortOption }));
      notification.showMessage(NOTIFICATION.CREATED);
    } catch (err) {
      console.log(err);
      // @ts-ignore

      notification.showMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onBackStep = () => {
    const prevStep = step - 1;
    if (prevStep === 2) {
      setStep(2);
    } else if (prevStep === 3) {
      if (analysts?.filter((user) => user.role.title === RolesLowerCase.ANALYST)?.length) {
        setStep(3);
      } else {
        setStep(2);
      }
    } else if (prevStep === 1) {
      setStep(1);
    }
  };

  const analystUserChips = useMemo(() => {
    const listOfChips =
      step === 2
        ? analysts
            .filter((user) => user?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN)
            .filter((u) => form?.analysts.includes(`${u.id}`))
        : analysts.filter((user) => user?.role?.title === RolesLowerCase.ANALYST).filter((u) => form?.analysts.includes(`${u.id}`));

    return listOfChips?.map((u) => (
      <div key={`chip_${u.id}`} className={styles.chip}>
        <span>{u?.username ? u.username : (canViewEmails ? u.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onAnalystAdd({ target: { id: u.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [form, analysts, step]);

  const viewerUserChips = useMemo(() => {
    const listOfChips = viewers.filter((u) => form?.viewers.includes(`${u.id}`));

    return listOfChips?.map((u) => (
      <div key={`chip_${u.id}`} className={styles.chip}>
        <span>{u?.username ? u.username : (canViewEmails ? u.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onAnalystAdd({ target: { id: u.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [form, analysts, step]);

  useHotkeys('Enter', () => {
    if (!open || loading || allDone) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        toThirdStepButtonRef?.current?.click();
        break;
      case 3:
        toFourthStepButtonRef?.current?.click();
        break;
      case 4:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  useHotkeys('Backspace', () => {
    if (!open || loading || allDone) return;

    switch (step) {
      case 2:
        backToFirstStepButtonRef?.current?.click();
        break;
      case 3:
        backToSecondStepButtonRef?.current?.click();
        break;
      case 4:
        backToThirdStepButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      {allDone ? (
        <div className={styles.successModal}>
          <div className={styles.title}>All Done 🎉</div>
          <ContainedButton onClick={onClose}>Close</ContainedButton>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            {step === 1 && <div className={styles.title}>Create a subgroup</div>}
            {step === 2 && <div className={styles.title}>Add Organisation Admin</div>}
            {step === 3 && <div className={styles.title}>Add Analyst</div>}
            {step === 4 && <div className={styles.title}>Add Viewer</div>}
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={step * 25} />

          {step === 1 && (
            <form onSubmit={handleSubmit(toOrgAdminStep)}>
              <input
                {...register('title', { required: 'enter subgroup name' })}
                className={styles.field}
                placeholder="Subgroup Name"
                type="text"
              />
              <ErrorMessage errors={errors} name="title" render={({ message }) => <div className="errorMessage">{message}</div>} />
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={toSecondStepButtonRef}>Next</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={onClose}>
                  Cancel
                </OutlinedButton>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit(toAnalystStep)}>
              <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={(e) => onSearch(e, 'analyst')} />
              <div className={styles.userChips}>{analystUserChips}</div>
              <div className={styles.usersList}>
                <label htmlFor="selectAllOa">
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <div className={styles.info}>
                        <div className={styles.name}>Select All</div>
                      </div>
                    </div>
                    <div className={styles.rightSide}>
                      <input
                        id="selectAllOa"
                        onChange={(e) => onSelectAll(e, RolesLowerCase.ORGANIZATION_ADMIN)}
                        type="checkbox"
                        checked={selectAllOa}
                      />
                    </div>
                  </div>
                </label>
                {analysts
                  .filter((user) => user?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN)
                  .map((orgAdmin, i) => (
                    <label htmlFor={orgAdmin.id} key={`${orgAdmin.id}_${i}`}>
                      <div className={styles.userLine}>
                        <div className={styles.userInfo}>
                          <Avatar avatar={orgAdmin.avatar_url} />
                          <div className={styles.info}>
                            <div className={styles.name}>{orgAdmin.username}</div>
                            {canViewEmails && (<Tooltip title={orgAdmin.email}>
                              <div className={styles.email}>{orgAdmin.email}</div>
                            </Tooltip>)}
                          </div>
                        </div>
                        <div className={styles.rightSide}>
                          <RolePlate userRole={orgAdmin.role.title} className={styles.rolePlate} />
                          <input onChange={onAnalystAdd} id={orgAdmin.id} type="checkbox" checked={form?.analysts.includes(orgAdmin.id)} />
                        </div>
                      </div>
                    </label>
                  ))}
              </div>
              <span className={styles.errMsg}>{errMsg}</span>
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={toThirdStepButtonRef}>Next</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={onBackStep}>
                  <div ref={backToFirstStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit(toViewersStep)}>
              <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={(e) => onSearch(e, 'analyst')} />
              <div className={styles.userChips}>{analystUserChips}</div>
              <div className={styles.usersList}>
                <label htmlFor="selectAllAnalysts">
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <div className={styles.info}>
                        <div className={styles.name}>Select All</div>
                      </div>
                    </div>
                    <div className={styles.rightSide}>
                      <input
                        id="selectAllAnalysts"
                        onChange={(e) => onSelectAll(e, RolesLowerCase.ANALYST)}
                        type="checkbox"
                        checked={selectAllAnalyst}
                      />
                    </div>
                  </div>
                </label>
                {analysts
                  .filter((user) => user?.role?.title === RolesLowerCase.ANALYST)
                  .map((analyst, i) => (
                    <label htmlFor={analyst.id} key={`${analyst.id}_${i}`}>
                      <div className={styles.userLine}>
                        <div className={styles.userInfo}>
                          <Avatar avatar={analyst.avatar_url} />
                          <div className={styles.info}>
                            <div className={styles.name}>{analyst.username}</div>
                            {canViewEmails && (<Tooltip title={analyst.email}>
                              <div className={styles.email}>{analyst.email}</div>
                            </Tooltip>)}
                          </div>
                        </div>
                        <div className={styles.rightSide}>
                          <RolePlate userRole={analyst.role.title} className={styles.rolePlate} />
                          <input onChange={onAnalystAdd} id={analyst.id} type="checkbox" checked={form?.analysts.includes(analyst.id)} />
                        </div>
                      </div>
                    </label>
                  ))}
              </div>
              <span className={styles.errMsg}>{errMsg}</span>
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={toFourthStepButtonRef}>Next</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={onBackStep}>
                  <div ref={backToSecondStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </form>
          )}

          {step === 4 && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={(e) => onSearch(e, 'viewer')} />
              <div className={styles.userChips}>{viewerUserChips}</div>
              <div className={styles.usersList}>
                <label htmlFor="selectAllViewers">
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <div className={styles.info}>
                        <div className={styles.name}>Select All</div>
                      </div>
                    </div>
                    <div className={styles.rightSide}>
                      <input
                        id="selectAllViewers"
                        onChange={(e) => onSelectAll(e, RolesLowerCase.VIEWER)}
                        type="checkbox"
                        checked={selectAllViewer}
                      />
                    </div>
                  </div>
                </label>
                {viewers.map((viewer, i) => (
                  <label htmlFor={viewer.id} key={`${viewer.id}_${i}`}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={viewer.avatar_url} />
                        <div className={styles.info}>
                          <div className={styles.name}>{viewer.username}</div>
                          {canViewEmails && (<Tooltip title={viewer.email}>
                            <div className={styles.email}>{viewer.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.rightSide}>
                        <RolePlate userRole={viewer.role.title} className={styles.rolePlate} />
                        <input onChange={onViewerAdd} id={viewer.id} type="checkbox" checked={form?.viewers.includes(viewer.id)} />
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <span className={styles.errMsg}>{errMsg}</span>
              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading} type="submit">
                  <div ref={submitButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={onBackStep}>
                  <div ref={backToThirdStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateSubGroupModal;
